import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, IconButton,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  LinearProgress, Alert, Tabs, Tab, Grid, Tooltip, Divider, CircularProgress,
} from '@mui/material';
import {
  CloudUpload, MusicNote, Delete, Edit, Search, CheckCircle, ErrorOutline,
  LibraryMusic, Refresh, Link as LinkIcon, LinkOff, QueueMusic, Clear,
} from '@mui/icons-material';
import axios from 'axios';
import { buildSearchHaystack, matchesSearchWords, searchWords } from '../utils/searchText';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
// Match the library bulk uploader: stay under the browser's ~6 connections
// per host so cookies/auth keep working while several FLACs transfer at once.
const MIX_TRACK_UPLOAD_CONCURRENCY = 5;
const MIX_TRACK_UPLOAD_TIMEOUT_MS = 30 * 60 * 1000;
const UPLOAD_JOB_POLL_MS = 3000;
const UPLOAD_JOB_LIST_LIMIT = 500;
// Failed polls retry every 3 × UPLOAD_JOB_POLL_MS; 40 of them is ~6 minutes,
// enough to ride out an updater restart without polling forever if the API
// is genuinely gone.
const UPLOAD_JOB_POLL_MAX_ERRORS = 40;

const ASSET_SEARCH_DEBOUNCE_MS = 250;
const ASSET_SEARCH_LIMIT = 50;

// Rekordbox names carry mix tags like "(Extended Mix)" that library titles
// usually lack, so they would make every word-match search miss.
function defaultAssetSearch(trackName) {
  const name = String(trackName || '').trim();
  let stripped = name;
  let previous;
  do {
    previous = stripped;
    stripped = stripped.replace(/\s*[([][^()[\]]*[)\]]\s*/g, ' ');
  } while (stripped !== previous);
  stripped = stripped.replace(/\s+/g, ' ').trim();
  return stripped || name;
}

const UPLOAD_JOB_STATUS = {
  queued: { label: 'Queued', color: 'default' },
  processing: { label: 'Normalising', color: 'info' },
  completed: { label: 'Ready', color: 'success' },
  duplicate: { label: 'Duplicate', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
};

function summarizeMixUploadJobs(jobs, jobIds) {
  const ids = new Set(jobIds || []);
  const batch = ids.size > 0
    ? (jobs || []).filter((job) => ids.has(job.id))
    : [];
  return batch.reduce((summary, job) => {
    if (job.status === 'failed') summary.failed += 1;
    else if (job.status === 'completed') summary.ready += 1;
    else if (job.status === 'duplicate') summary.duplicates += 1;
    return summary;
  }, { failed: 0, ready: 0, duplicates: 0 });
}

export default function BeatmixManager({ stationId, categories }) {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [xmlFiles, setXmlFiles] = useState([]);
  const [rekordboxTracks, setRekordboxTracks] = useState([]);
  const [selectedXmlFile, setSelectedXmlFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [alert, setAlert] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkingTrack, setLinkingTrack] = useState(null);
  const [searchAssets, setSearchAssets] = useState([]);
  const [assetSearch, setAssetSearch] = useState('');
  const [assetSearchLoading, setAssetSearchLoading] = useState(false);
  const [trackSearch, setTrackSearch] = useState('');
  const [trackLinkFilter, setTrackLinkFilter] = useState('all');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  // Background normalisation jobs for mix-track uploads (see /api/beatmix/upload-jobs).
  const [uploadJobs, setUploadJobs] = useState([]);
  const [uploadJobsActive, setUploadJobsActive] = useState(0);
  const [uploadJobsPollToken, setUploadJobsPollToken] = useState(0);
  // Session confirmation for Mix Track Upload: files on the server vs still
  // processing in the background. Survives the transfer bar going away.
  const [mixTransfer, setMixTransfer] = useState(null);
  const previousActiveJobsRef = useRef(0);
  const trackedMixJobIdsRef = useRef([]);

  const fetchXmlFiles = useCallback(async () => {
    if (!stationId) return;
    try {
      const res = await axios.get(`${API_URL}/api/beatmix/rekordbox/files`, {
        params: { station_id: stationId }, withCredentials: true,
      });
      setXmlFiles(res.data);
    } catch (err) {
      console.error('Error fetching XML files:', err);
    }
  }, [stationId]);

  const fetchTracks = useCallback(async (xmlFileId = null) => {
    if (!stationId) return;
    try {
      const params = { station_id: stationId };
      if (xmlFileId) params.xml_file_id = xmlFileId;
      const res = await axios.get(`${API_URL}/api/beatmix/rekordbox/tracks`, {
        params, withCredentials: true,
      });
      setRekordboxTracks(res.data);
    } catch (err) {
      console.error('Error fetching tracks:', err);
    }
  }, [stationId]);

  useEffect(() => {
    fetchXmlFiles();
    fetchTracks();
  }, [fetchXmlFiles, fetchTracks]);

  const fetchUploadJobs = useCallback(async () => {
    if (!stationId) return { jobs: [], active: 0 };
    try {
      const res = await axios.get(`${API_URL}/api/beatmix/upload-jobs`, {
        params: { station_id: stationId, limit: UPLOAD_JOB_LIST_LIMIT }, withCredentials: true,
      });
      const data = res.data || {};
      setUploadJobs(Array.isArray(data.jobs) ? data.jobs : []);
      setUploadJobsActive(Number(data.active) || 0);
      return data;
    } catch (err) {
      console.error('Error fetching upload jobs:', err);
      // Leave the last known status on screen; the caller decides whether to
      // try again (a backend restart mid-batch must not freeze the panel).
      return { jobs: null, active: null, error: true };
    }
  }, [stationId]);

  // Poll the background queue while anything is still queued/normalising.
  // Bumping `uploadJobsPollToken` (after an upload) restarts the loop.
  useEffect(() => {
    if (!stationId) return undefined;
    let cancelled = false;
    let timer = null;
    let consecutiveErrors = 0;
    const poll = async () => {
      const data = await fetchUploadJobs();
      if (cancelled) return;
      if (data.error) {
        // Transient failure (e.g. the backend is restarting): keep trying at a
        // slower rate for a few minutes rather than stopping with jobs shown
        // as still active.
        consecutiveErrors += 1;
        if (consecutiveErrors <= UPLOAD_JOB_POLL_MAX_ERRORS) {
          timer = setTimeout(poll, UPLOAD_JOB_POLL_MS * 3);
        }
        return;
      }
      consecutiveErrors = 0;
      if ((Number(data.active) || 0) > 0) {
        timer = setTimeout(poll, UPLOAD_JOB_POLL_MS);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [stationId, fetchUploadJobs, uploadJobsPollToken]);

  // When the last background job finishes, refresh the Rekordbox link table so
  // the newly normalised tracks show their auto-matched cue points, and confirm
  // on the Mix Track tab that processing is done.
  useEffect(() => {
    if (previousActiveJobsRef.current > 0 && uploadJobsActive === 0) {
      fetchTracks();
      if (trackedMixJobIdsRef.current.length > 0) {
        const summary = summarizeMixUploadJobs(uploadJobs, trackedMixJobIdsRef.current);
        setMixTransfer((prev) => (
          prev && prev.phase !== 'uploading'
            ? {
              ...prev,
              phase: 'processed',
              processingFailed: summary.failed,
              processingReady: summary.ready,
              processingDuplicates: summary.duplicates,
            }
            : prev
        ));
        if (summary.failed > 0) {
          setAlert({
            type: summary.ready > 0 ? 'warning' : 'error',
            message: `Background processing finished: ${summary.ready} ready, ${summary.failed} failed. Check the upload list for details.`,
          });
        } else {
          setAlert({
            type: 'success',
            message: `Background processing finished. ${summary.ready} mix track${summary.ready === 1 ? '' : 's'} ready in the library.`,
          });
        }
      }
      trackedMixJobIdsRef.current = [];
    }
    previousActiveJobsRef.current = uploadJobsActive;
  }, [uploadJobsActive, uploadJobs, fetchTracks]);

  const handleXmlUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('xmlFile', file);
      formData.append('station_id', stationId);
      const res = await axios.post(`${API_URL}/api/beatmix/rekordbox/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => setUploadProgress(Math.round((p.loaded / p.total) * 100)),
      });
      const { total_tracks, matched_tracks, updated_tracks } = res.data;
      let msg = `Uploaded ${total_tracks} tracks, ${matched_tracks} matched to library`;
      if (updated_tracks > 0) msg += `, ${updated_tracks} updated from previous XMLs`;
      setAlert({ type: 'success', message: msg });
      fetchXmlFiles();
      fetchTracks();
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.message || 'Upload failed' });
    } finally {
      setLoading(false);
      setUploadProgress(null);
      e.target.value = '';
    }
  };

  const handleMixTrackUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    e.target.value = '';
    setLoading(true);
    setUploadProgress(0);

    const MAX_ATTEMPTS = 3;
    let totalUploaded = 0;
    let totalFailed = 0;
    let totalDuplicates = 0;
    let chunkFailures = 0;
    let pollStarted = false;

    try {
      let filesToUpload = files;
      try {
        const check = await axios.post(`${API_URL}/api/beatmix/check-duplicates`, {
          station_id: stationId,
          filenames: files.map((file) => file.name),
        }, { withCredentials: true });
        const uniqueNames = [...(check.data.unique || [])];
        totalDuplicates = (check.data.duplicates || []).length;
        filesToUpload = files.filter((file) => {
          const index = uniqueNames.indexOf(file.name);
          if (index === -1) return false;
          uniqueNames.splice(index, 1);
          return true;
        });
      } catch (err) {
        console.warn('Duplicate pre-check failed, uploading all selected files', err);
      }

      if (filesToUpload.length === 0) {
        setMixTransfer(null);
        setAlert({
          type: 'info',
          message: `Skipped ${totalDuplicates} duplicate${totalDuplicates === 1 ? '' : 's'} — nothing new to upload`,
        });
        return;
      }

      if (uploadJobsActive === 0) trackedMixJobIdsRef.current = [];
      const transferState = {
        phase: 'uploading',
        total: filesToUpload.length,
        sent: 0,
        queued: 0,
        failed: 0,
        duplicates: totalDuplicates,
        jobIds: [],
        processingFailed: 0,
        processingReady: 0,
        processingDuplicates: 0,
      };
      setMixTransfer({ ...transferState });

      const fileProgress = new Array(filesToUpload.length).fill(0);
      const updateProgress = () => {
        const loaded = fileProgress.reduce((sum, value) => sum + value, 0);
        setUploadProgress(Math.min(99, Math.round((loaded / filesToUpload.length) * 100)));
      };
      const startJobPolling = () => {
        if (pollStarted) return;
        pollStarted = true;
        setUploadJobsPollToken((token) => token + 1);
      };
      const publishTransfer = (patch) => {
        Object.assign(transferState, patch);
        setMixTransfer({ ...transferState });
      };

      const uploadFile = async (file, fileIndex) => {
        const formData = new FormData();
        formData.append('audioFiles', file);
        formData.append('station_id', stationId);
        if (selectedCategoryIds.length > 0) {
          formData.append('category_ids', JSON.stringify(selectedCategoryIds));
        }
        // Let the browser set multipart Content-Type including the boundary.
        // The server answers as soon as the file is on disk; loudness
        // normalisation and cue matching continue in its background queue.
        const res = await axios.post(`${API_URL}/api/beatmix/upload`, formData, {
          withCredentials: true,
          timeout: MIX_TRACK_UPLOAD_TIMEOUT_MS,
          onUploadProgress: (p) => {
            if (!p.total) return;
            fileProgress[fileIndex] = p.loaded / p.total;
            updateProgress();
          },
        });
        fileProgress[fileIndex] = 1;
        updateProgress();
        const queued = res.data.queued ?? res.data.uploaded ?? 0;
        const failed = res.data.failed || 0;
        const duplicates = res.data.duplicates || 0;
        totalUploaded += queued;
        totalFailed += failed;
        totalDuplicates += duplicates;
        const jobIds = (Array.isArray(res.data.results) ? res.data.results : [])
          .filter((result) => result.queued && result.job_id)
          .map((result) => result.job_id);
        trackedMixJobIdsRef.current = [...trackedMixJobIdsRef.current, ...jobIds];
        publishTransfer({
          sent: transferState.sent + 1,
          queued: transferState.queued + queued,
          failed: transferState.failed + failed,
          duplicates: transferState.duplicates + duplicates,
          jobIds: [...transferState.jobIds, ...jobIds],
        });
        startJobPolling();
      };

      const isRetryableUploadError = (err) => {
        if (!err || err.response) return false;
        const code = err.code || '';
        if (code === 'ERR_CANCELED' || err.name === 'CanceledError') return false;
        return code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ECONNRESET' || !code;
      };

      const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      let nextIndex = 0;
      const workerCount = Math.min(MIX_TRACK_UPLOAD_CONCURRENCY, filesToUpload.length);
      const runWorker = async () => {
        while (true) {
          const i = nextIndex;
          nextIndex += 1;
          if (i >= filesToUpload.length) return;
          let lastErr = null;
          for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
            try {
              await uploadFile(filesToUpload[i], i);
              lastErr = null;
              break;
            } catch (err) {
              lastErr = err;
              fileProgress[i] = 0;
              updateProgress();
              console.warn(`Mix track upload failed for "${filesToUpload[i].name}" (attempt ${attempt}/${MAX_ATTEMPTS})`, err);
              if (attempt < MAX_ATTEMPTS && isRetryableUploadError(err)) {
                await sleep(1500 * attempt);
                continue;
              }
              break;
            }
          }
          if (lastErr) {
            chunkFailures += 1;
            totalFailed += 1;
            publishTransfer({ failed: transferState.failed + 1 });
          }
        }
      };
      await Promise.all(Array.from({ length: workerCount }, () => runWorker()));

      setUploadProgress(100);
      startJobPolling();
      let stillProcessing = uploadJobsActive > 0;
      let processingSummary = { failed: 0, ready: 0, duplicates: 0 };
      try {
        const status = await fetchUploadJobs();
        if (status.error) {
          stillProcessing = totalUploaded > 0;
        } else {
          stillProcessing = (Number(status.active) || 0) > 0;
          processingSummary = summarizeMixUploadJobs(status.jobs, transferState.jobIds);
        }
      } catch (_) {
        stillProcessing = true;
      }
      publishTransfer({
        phase: stillProcessing && totalUploaded > 0 ? 'complete' : 'processed',
        processingFailed: processingSummary.failed,
        processingReady: processingSummary.ready,
        processingDuplicates: processingSummary.duplicates,
      });

      if (totalUploaded === 0 && chunkFailures > 0 && totalDuplicates === 0) {
        setAlert({ type: 'error', message: 'Upload failed' });
        return;
      }

      let msg;
      if (totalUploaded > 0 && !stillProcessing) {
        msg = processingSummary.failed > 0
          ? `Background processing finished: ${processingSummary.ready} ready, ${processingSummary.failed} failed. Check the upload list for details.`
          : `Background processing finished. ${processingSummary.ready} mix track${processingSummary.ready === 1 ? '' : 's'} ready in the library.`;
      } else {
        msg = totalUploaded > 0
          ? `${totalUploaded} mix track${totalUploaded === 1 ? '' : 's'} ${totalUploaded === 1 ? 'is' : 'are'} on the server. Processing them automatically in the background — you can leave this page.`
          : 'No new mix tracks were uploaded';
      }
      if (totalDuplicates > 0) msg += ` Skipped ${totalDuplicates} duplicate${totalDuplicates === 1 ? '' : 's'}.`;
      if (totalFailed > 0) msg += ` ${totalFailed} failed.`;
      setAlert({
        type: processingSummary.failed > 0 && processingSummary.ready === 0
          ? 'error'
          : ((processingSummary.failed > 0 || totalFailed > 0 || chunkFailures > 0) ? 'warning' : 'success'),
        message: msg,
      });
      startJobPolling();
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.message || 'Upload failed' });
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const handleRematch = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/beatmix/rekordbox/rematch`,
        { station_id: stationId }, { withCredentials: true });
      setAlert({ type: 'success', message: `Re-matched ${res.data.newly_matched} tracks` });
      fetchTracks();
    } catch (err) {
      setAlert({ type: 'error', message: 'Re-match failed' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLinkDialog = (track) => {
    setLinkingTrack(track);
    setSearchAssets([]);
    setAssetSearchLoading(true);
    setAssetSearch(defaultAssetSearch(track.track_name));
    setLinkDialogOpen(true);
  };

  useEffect(() => {
    if (!linkDialogOpen || !stationId) return undefined;
    let cancelled = false;
    setAssetSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/beatmix/assets`, {
          params: { station_id: stationId, search: assetSearch.trim(), limit: ASSET_SEARCH_LIMIT },
          withCredentials: true,
        });
        if (!cancelled) setSearchAssets(res.data);
      } catch (err) {
        if (!cancelled) setSearchAssets([]);
      } finally {
        if (!cancelled) setAssetSearchLoading(false);
      }
    }, ASSET_SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [linkDialogOpen, stationId, assetSearch]);

  const trackLinkCounts = useMemo(() => {
    const linked = rekordboxTracks.filter((track) => track.asset_id).length;
    return { all: rekordboxTracks.length, linked, unlinked: rekordboxTracks.length - linked };
  }, [rekordboxTracks]);

  const trackHaystacks = useMemo(() => new Map(rekordboxTracks.map((track) => [
    track.id,
    buildSearchHaystack([
      track.track_name, track.track_artist, track.tonality, track.asset_title, track.asset_artist,
    ]),
  ])), [rekordboxTracks]);

  const visibleTracks = useMemo(() => {
    const words = searchWords(trackSearch);
    return rekordboxTracks.filter((track) => {
      if (trackLinkFilter === 'linked' && !track.asset_id) return false;
      if (trackLinkFilter === 'unlinked' && track.asset_id) return false;
      if (words.length === 0) return true;
      return matchesSearchWords(trackHaystacks.get(track.id) || '', words);
    });
  }, [rekordboxTracks, trackHaystacks, trackSearch, trackLinkFilter]);

  const handleLinkTrack = async (assetId) => {
    if (!linkingTrack) return;
    try {
      const res = await axios.put(`${API_URL}/api/beatmix/rekordbox/tracks/${linkingTrack.id}/link`,
        { asset_id: assetId }, { withCredentials: true });
      const linkedTitle = res.data?.asset_title || 'library asset';
      setRekordboxTracks((current) => current.map((track) => (
        track.id === linkingTrack.id
          ? {
              ...track,
              asset_id: res.data?.asset_id || assetId,
              asset_title: linkedTitle,
              asset_has_mix_cues: !!(res.data?.asset_has_mix_cues || res.data?.marker_cues_applied),
            }
          : track
      )));
      if (res.data?.warning) {
        setAlert({ type: 'warning', message: res.data.warning });
      } else if (res.data?.marker_cues_applied) {
        setAlert({ type: 'success', message: `Linked to "${linkedTitle}" and copied mix cue points` });
      } else {
        setAlert({ type: 'success', message: `Linked to "${linkedTitle}"` });
      }
      setLinkDialogOpen(false);
      fetchTracks();
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.message || 'Failed to link track' });
    }
  };

  const cardSx = { bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', borderRadius: 2 };
  const tableCellSx = { color: 'var(--rs-text)', borderBottom: '1px solid var(--rs-border)' };
  const uploadJobCounts = uploadJobs.reduce((acc, job) => {
    acc[job.status] = (acc[job.status] || 0) + 1;
    return acc;
  }, { queued: 0, processing: 0, completed: 0, duplicate: 0, failed: 0 });

  if (!stationId) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please select a station to manage beat mixing.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {alert && (
        <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label="Rekordbox XML" icon={<LibraryMusic />} iconPosition="start" />
        <Tab label="Mix Track Upload" icon={<CloudUpload />} iconPosition="start" />
        <Tab label="Cue Point Status" icon={<QueueMusic />} iconPosition="start" />
      </Tabs>

      {/* Rekordbox XML Tab */}
      {tabValue === 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button variant="contained" component="label" startIcon={<CloudUpload />}
              sx={{ bgcolor: '#7c4dff' }}>
              Upload Rekordbox XML
              <input type="file" hidden accept=".xml" onChange={handleXmlUpload} />
            </Button>
            <Button variant="outlined" startIcon={<Refresh />} onClick={handleRematch}
              disabled={loading} sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}>
              Re-match Tracks
            </Button>
            {xmlFiles.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {xmlFiles.length} XML file{xmlFiles.length !== 1 ? 's' : ''} imported ({rekordboxTracks.length} tracks total)
              </Typography>
            )}
          </Box>

          {loading && uploadProgress !== null && (
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 2 }} />
          )}

          {rekordboxTracks.length > 0 && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
              <TextField
                size="small"
                placeholder="Search by track, artist, key or linked asset..."
                value={trackSearch}
                onChange={(e) => setTrackSearch(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                  endAdornment: trackSearch ? (
                    <IconButton size="small" onClick={() => setTrackSearch('')} aria-label="Clear search">
                      <Clear fontSize="small" />
                    </IconButton>
                  ) : null,
                }}
                sx={{
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    color: 'var(--rs-text)',
                    backgroundColor: 'var(--rs-overlay-soft)',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#22d3ee' },
                  },
                }}
              />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel sx={{ color: '#999' }}>Show</InputLabel>
                <Select value={trackLinkFilter} label="Show" onChange={(e) => setTrackLinkFilter(e.target.value)}
                  sx={{ color: 'var(--rs-text)' }}>
                  <MenuItem value="all">All tracks ({trackLinkCounts.all})</MenuItem>
                  <MenuItem value="unlinked">Unlinked ({trackLinkCounts.unlinked})</MenuItem>
                  <MenuItem value="linked">Linked ({trackLinkCounts.linked})</MenuItem>
                </Select>
              </FormControl>
              {visibleTracks.length !== rekordboxTracks.length && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  Showing {visibleTracks.length} of {rekordboxTracks.length}
                </Typography>
              )}
            </Box>
          )}

          <TableContainer component={Paper} sx={cardSx}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Track Name</TableCell>
                  <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Artist</TableCell>
                  <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>BPM</TableCell>
                  <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Key</TableCell>
                  <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Cue In</TableCell>
                  <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Mix Done</TableCell>
                  <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Outro Start</TableCell>
                  <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Mix End</TableCell>
                  <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Linked Asset</TableCell>
                  <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rekordboxTracks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ ...tableCellSx, textAlign: 'center', color: '#999' }}>
                      No rekordbox tracks found. Upload a Rekordbox XML file to get started.
                    </TableCell>
                  </TableRow>
                ) : visibleTracks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ ...tableCellSx, textAlign: 'center', color: '#999' }}>
                      No tracks match this search.
                    </TableCell>
                  </TableRow>
                ) : visibleTracks.map((track) => (
                  <TableRow key={track.id} sx={{ '&:hover': { bgcolor: 'var(--rs-row)' } }}>
                    <TableCell sx={tableCellSx}>{track.track_name}</TableCell>
                    <TableCell sx={tableCellSx}>{track.track_artist || '-'}</TableCell>
                    <TableCell sx={tableCellSx}>{track.bpm ? track.bpm.toFixed(1) : '-'}</TableCell>
                    <TableCell sx={tableCellSx}>{track.tonality || '-'}</TableCell>
                    <TableCell sx={tableCellSx}>{track.cue_in ? track.cue_in.toFixed(2) + 's' : '-'}</TableCell>
                    <TableCell sx={tableCellSx}>{track.mix_done ? track.mix_done.toFixed(2) + 's' : '-'}</TableCell>
                    <TableCell sx={tableCellSx}>{track.outro_start ? track.outro_start.toFixed(2) + 's' : '-'}</TableCell>
                    <TableCell sx={tableCellSx}>{track.mix_end ? track.mix_end.toFixed(2) + 's' : '-'}</TableCell>
                    <TableCell sx={tableCellSx}>
                      {track.asset_id ? (
                        <Chip
                          label={track.asset_title || `Asset #${track.asset_id}`}
                          icon={track.asset_has_mix_cues === false ? <ErrorOutline /> : <CheckCircle />}
                          size="small"
                          color={track.asset_has_mix_cues === false ? 'warning' : 'success'}
                          variant="outlined"
                          title={track.asset_has_mix_cues === false
                            ? 'Linked, but mix cue points were not copied to this library asset'
                            : undefined}
                        />
                      ) : (
                        <Chip label="Unlinked" icon={<ErrorOutline />} size="small" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <Tooltip title={track.asset_id ? 'Re-link to different asset' : 'Link to library asset'}>
                        <IconButton size="small" onClick={() => handleOpenLinkDialog(track)}
                          sx={{ color: '#22d3ee' }}>
                          {track.asset_id ? <LinkIcon /> : <LinkOff />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Mix Track Upload Tab */}
      {tabValue === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Select a whole folder of mix tracks — 200 at a time is fine. Files copy to this
            machine first; loudness normalisation and Rekordbox cue matching then run
            automatically in the background. Keep this tab open until the upload bar finishes.
            After that you can leave; processing continues on the server and is tracked below.
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel sx={{ color: '#999' }}>Categories</InputLabel>
              <Select
                multiple
                value={selectedCategoryIds}
                onChange={(e) => setSelectedCategoryIds(e.target.value)}
                label="Categories"
                sx={{ color: 'var(--rs-text)' }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const cat = (categories || []).find(c => c.id === id);
                      return <Chip key={id} label={cat?.name || id} size="small" sx={{ bgcolor: '#7c4dff30', color: 'var(--rs-text)' }} />;
                    })}
                  </Box>
                )}
                MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
              >
                {(categories || []).map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" component="label" startIcon={<CloudUpload />}
              disabled={loading} sx={{ bgcolor: '#7c4dff' }}>
              Upload Mix Tracks
              <input type="file" hidden accept=".mp3,.wav,.flac,.ogg,.opus,.m4a,.aac" multiple onChange={handleMixTrackUpload} />
            </Button>
          </Box>

          {loading && uploadProgress !== null && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: 'var(--rs-text)', mb: 0.5 }}>
                {mixTransfer
                  ? `Uploading to the server — ${mixTransfer.sent} of ${mixTransfer.total} file${mixTransfer.total === 1 ? '' : 's'} accepted`
                  : `Uploading mix tracks… ${uploadProgress}%`}
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {mixTransfer && mixTransfer.phase !== 'uploading' && (
            <Alert
              severity={mixTransfer.phase === 'processed'
                ? (mixTransfer.processingFailed > 0
                  ? (mixTransfer.processingReady > 0 ? 'warning' : 'error')
                  : 'success')
                : (mixTransfer.failed > 0 && mixTransfer.queued === 0 ? 'error' : (mixTransfer.failed > 0 ? 'warning' : 'success'))}
              icon={mixTransfer.phase === 'processed' && mixTransfer.processingFailed > 0
                ? <ErrorOutline />
                : (mixTransfer.phase === 'processed' || uploadJobsActive === 0
                  ? <CheckCircle />
                  : <CircularProgress size={20} />)}
              onClose={() => setMixTransfer(null)}
              sx={{ mb: 2 }}
            >
              {mixTransfer.phase === 'processed' ? (
                <>
                  Background processing finished.
                  {` ${mixTransfer.processingReady || 0} ready.`}
                  {mixTransfer.processingDuplicates > 0
                    ? ` ${mixTransfer.processingDuplicates} duplicate${mixTransfer.processingDuplicates === 1 ? '' : 's'}.`
                    : ''}
                  {mixTransfer.processingFailed > 0
                    ? ` ${mixTransfer.processingFailed} failed — check the list below for details.`
                    : ''}
                </>
              ) : mixTransfer.queued > 0 ? (
                <>
                  {mixTransfer.queued} mix track{mixTransfer.queued === 1 ? '' : 's'} {mixTransfer.queued === 1 ? 'is' : 'are'} on the server.
                  {' '}Processing them automatically in the background — you can leave this page.
                  {uploadJobsActive > 0
                    ? ` ${uploadJobsActive} still normalising.`
                    : ' Status is listed below.'}
                </>
              ) : (
                <>Upload finished, but no new mix tracks were queued.</>
              )}
              {mixTransfer.duplicates > 0 ? ` Skipped ${mixTransfer.duplicates} duplicate${mixTransfer.duplicates === 1 ? '' : 's'}.` : ''}
              {mixTransfer.failed > 0 ? ` ${mixTransfer.failed} failed.` : ''}
            </Alert>
          )}

          {uploadJobs.length > 0 && (
            <TableContainer component={Paper} sx={{ ...cardSx, mb: 2, maxHeight: 480 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, flexWrap: 'wrap', position: 'sticky', top: 0, bgcolor: 'var(--rs-paper)', zIndex: 1 }}>
                {uploadJobsActive > 0 && <CircularProgress size={16} />}
                <Typography variant="subtitle2" sx={{ color: 'var(--rs-text)' }}>
                  {uploadJobsActive > 0
                    ? `Background processing — ${uploadJobsActive} of ${uploadJobs.length} still running automatically`
                    : (uploadJobCounts.failed > 0
                      ? `Background processing finished — ${uploadJobCounts.failed} failed`
                      : 'Background processing finished — all recent uploads are ready')}
                </Typography>
                <Chip size="small" label={`${uploadJobCounts.queued} queued`} variant="outlined" />
                <Chip size="small" label={`${uploadJobCounts.processing} normalising`} color="info" variant="outlined" />
                <Chip size="small" label={`${uploadJobCounts.completed} ready`} color="success" variant="outlined" />
                {uploadJobCounts.failed > 0 && (
                  <Chip size="small" label={`${uploadJobCounts.failed} failed`} color="error" variant="outlined" />
                )}
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Refresh status">
                  <IconButton size="small" onClick={fetchUploadJobs} sx={{ color: '#22d3ee' }}>
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>File</TableCell>
                    <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Track</TableCell>
                    <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uploadJobs.map((job) => {
                    const status = UPLOAD_JOB_STATUS[job.status] || { label: job.status, color: 'default' };
                    const trackName = [job.artist, job.title].filter(Boolean).join(' - ') || '-';
                    let detail = '';
                    if (job.status === 'failed') detail = job.error_message || 'Processing failed';
                    else if (job.status === 'duplicate') detail = job.asset_id ? `Already in library as asset #${job.asset_id}` : 'Already in library';
                    else if (job.status === 'completed') detail = job.asset_id ? `Library asset #${job.asset_id}` : 'Added to library';
                    else if (job.status === 'processing') detail = 'Loudness normalisation and canonical encode';
                    else detail = 'Waiting for a free encoder slot';
                    return (
                      <TableRow key={job.id} sx={{ '&:hover': { bgcolor: 'var(--rs-row)' } }}>
                        <TableCell sx={{ ...tableCellSx, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <span title={job.original_filename}>{job.original_filename}</span>
                        </TableCell>
                        <TableCell sx={tableCellSx}>{trackName}</TableCell>
                        <TableCell sx={tableCellSx}>
                          <Chip
                            label={status.label}
                            size="small"
                            color={status.color}
                            variant="outlined"
                            icon={job.status === 'processing' ? <CircularProgress size={12} /> : undefined}
                          />
                        </TableCell>
                        <TableCell sx={{ ...tableCellSx, color: job.status === 'failed' ? '#f87171' : 'var(--rs-text)' }}>
                          {detail}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography variant="body2" color="text.secondary">
            Supported formats: MP3, WAV, FLAC, OGG/Opus, M4A, AAC. Select many files at once
            (around 200 is expected). Duplicates are skipped. Once a file is on this machine it
            is processed automatically and linked to Rekordbox cue points if a matching XML entry exists.
          </Typography>
        </Box>
      )}

      {/* Cue Point Status Tab */}
      {tabValue === 2 && (
        <CuePointStatus stationId={stationId} />
      )}

      {/* Link Track Dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--rs-paper)', color: 'var(--rs-text)' } }}>
        <DialogTitle>
          Link Rekordbox Track to Library Asset
          {linkingTrack && (
            <Typography variant="body2" color="text.secondary">
              Rekordbox: {linkingTrack.track_name} {linkingTrack.track_artist ? `- ${linkingTrack.track_artist}` : ''}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <TextField fullWidth size="small" autoFocus placeholder="Search library by title, artist or filename..."
              value={assetSearch} onChange={(e) => setAssetSearch(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                endAdornment: assetSearchLoading ? (
                  <CircularProgress size={16} />
                ) : assetSearch ? (
                  <IconButton size="small" onClick={() => setAssetSearch('')} aria-label="Clear search">
                    <Clear fontSize="small" />
                  </IconButton>
                ) : null,
              }}
              sx={{ '& .MuiInputBase-root': { color: 'var(--rs-text)' } }} />
          </Box>
          <TableContainer component={Paper} sx={cardSx}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Title</TableCell>
                  <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Artist</TableCell>
                  <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchAssets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ ...tableCellSx, textAlign: 'center', color: '#999' }}>
                      {assetSearchLoading
                        ? 'Searching…'
                        : 'No matching library assets. Try fewer words, or just the artist or part of the title.'}
                    </TableCell>
                  </TableRow>
                ) : searchAssets.map((asset) => (
                  <TableRow key={asset.id}>
                    <TableCell sx={tableCellSx}>{asset.title}</TableCell>
                    <TableCell sx={tableCellSx}>{asset.artist || '-'}</TableCell>
                    <TableCell sx={tableCellSx}>
                      <Button size="small" variant="contained" onClick={() => handleLinkTrack(asset.id)}
                        sx={{ bgcolor: '#7c4dff' }}>
                        Link
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function CuePointStatus({ stationId }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [downbeatStatus, setDownbeatStatus] = useState(null);
  const [savingAssetId, setSavingAssetId] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchAssets = useCallback(async () => {
    if (!stationId) return;
    setLoading(true);
    try {
      const params = { station_id: stationId };
      if (filter === 'with_cues') params.has_cues_only = 'true';
      const [assetsRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/beatmix/assets`, {
          params, withCredentials: true,
        }),
        axios.get(`${API_URL}/api/beatmix/downbeat-status`, {
          params: { station_id: stationId },
          withCredentials: true,
        }),
      ]);
      setAssets(assetsRes.data);
      setDownbeatStatus(statusRes.data);
    } catch (err) {
      console.error('Error fetching beatmix assets:', err);
    } finally {
      setLoading(false);
    }
  }, [stationId, filter]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const handleAnalyseLibrary = async () => {
    try {
      await axios.post(
        `${API_URL}/api/beatmix/analyse-downbeats`,
        {},
        { withCredentials: true }
      );
      setMessage({ severity: 'info', text: 'Library downbeat analysis started in the background.' });
    } catch (err) {
      setMessage({ severity: 'error', text: err.response?.data?.message || 'Could not start analysis.' });
    }
  };

  const handleReanalyse = async (asset) => {
    setSavingAssetId(asset.id);
    try {
      await axios.post(
        `${API_URL}/api/beatmix/analyse-downbeats/${asset.id}`,
        {},
        { withCredentials: true }
      );
      setAssets((current) => current.map((item) => (
        item.id === asset.id ? { ...item, mix_beat_meta: null } : item
      )));
      setMessage({ severity: 'info', text: `${asset.title} was queued for fresh analysis.` });
    } catch (err) {
      setMessage({ severity: 'error', text: err.response?.data?.message || 'Could not queue analysis.' });
    } finally {
      setSavingAssetId(null);
    }
  };

  const handleRotationChange = async (asset, value) => {
    setSavingAssetId(asset.id);
    try {
      const body = value === 'auto'
        ? { mode: 'automatic', rotation_beats: null }
        : { rotation_beats: Number(value) };
      const res = await axios.put(
        `${API_URL}/api/beatmix/assets/${asset.id}/beat-rotation`,
        body,
        { withCredentials: true }
      );
      setAssets((current) => current.map((item) => (
        item.id === asset.id
          ? { ...item, mix_beat_meta: res.data.mix_beat_meta }
          : item
      )));
      setMessage({
        severity: 'success',
        text: value === 'auto'
          ? `${asset.title} returned to safe automatic mode.`
          : `${asset.title} now uses manual rotation +${value}.`,
      });
    } catch (err) {
      setMessage({ severity: 'error', text: err.response?.data?.message || 'Could not save rotation.' });
    } finally {
      setSavingAssetId(null);
    }
  };

  const beatMeta = (asset) => {
    if (!asset.mix_beat_meta) return null;
    if (typeof asset.mix_beat_meta === 'object') return asset.mix_beat_meta;
    try { return JSON.parse(asset.mix_beat_meta); } catch (_) { return null; }
  };

  const statusChip = (asset) => {
    const meta = beatMeta(asset);
    if (!meta) return <Chip label="Pending" size="small" variant="outlined" />;
    if (meta.source === 'manual') {
      return <Chip label={`Manual +${meta.rotationBeats}`} size="small" color="secondary" variant="outlined" />;
    }
    if (meta.status === 'ok' && Number(meta.confidence) >= 0.72) {
      return (
        <Chip
          label={`Auto +${meta.rotationBeats} • ${Math.round(Number(meta.confidence) * 100)}%`}
          size="small"
          color="success"
          variant="outlined"
        />
      );
    }
    if (meta.status === 'ambiguous') {
      return <Chip label="Review needed" size="small" color="warning" variant="outlined" />;
    }
    return <Chip label="Marker lock" size="small" variant="outlined" />;
  };

  const tableCellSx = { color: 'var(--rs-text)', borderBottom: '1px solid var(--rs-border)' };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel sx={{ color: '#999' }}>Filter</InputLabel>
          <Select value={filter} label="Filter" onChange={(e) => setFilter(e.target.value)}
            sx={{ color: 'var(--rs-text)' }}>
            <MenuItem value="all">All Assets</MenuItem>
            <MenuItem value="with_cues">With Cue Points</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchAssets} disabled={loading}
          sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}>
          Refresh
        </Button>
        <Button variant="outlined" startIcon={<MusicNote />} onClick={handleAnalyseLibrary}
          sx={{ borderColor: '#a78bfa', color: '#a78bfa' }}>
          Analyse library
        </Button>
      </Box>

      {message && (
        <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}
      {downbeatStatus && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Beat-in-bar: {downbeatStatus.automatic || 0} automatic, {downbeatStatus.manual || 0} manual,
          {' '}{downbeatStatus.ambiguous || 0} need review, {downbeatStatus.pending || 0} pending
        </Typography>
      )}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Title</TableCell>
              <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Artist</TableCell>
              <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Cue Points</TableCell>
              <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>BPM</TableCell>
              <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Key</TableCell>
              <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Downbeat</TableCell>
              <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Beat Rotation</TableCell>
              <TableCell sx={{ ...tableCellSx, fontWeight: 'bold' }}>Mix Version</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ ...tableCellSx, textAlign: 'center', color: '#999' }}>
                  No assets found
                </TableCell>
              </TableRow>
            ) : assets.map((asset) => (
              <TableRow key={asset.id} sx={{ '&:hover': { bgcolor: 'var(--rs-row)' } }}>
                <TableCell sx={tableCellSx}>{asset.title}</TableCell>
                <TableCell sx={tableCellSx}>{asset.artist || '-'}</TableCell>
                <TableCell sx={tableCellSx}>
                  {asset.has_mix_cues ? (
                    <Chip label="Yes" icon={<CheckCircle />} size="small" color="success" variant="outlined" />
                  ) : (
                    <Chip label="No" icon={<ErrorOutline />} size="small" color="default" variant="outlined" />
                  )}
                </TableCell>
                <TableCell sx={tableCellSx}>{asset.mix_bpm ? asset.mix_bpm.toFixed(1) : '-'}</TableCell>
                <TableCell sx={tableCellSx}>{asset.mix_key || '-'}</TableCell>
                <TableCell sx={tableCellSx}>{statusChip(asset)}</TableCell>
                <TableCell sx={tableCellSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Select
                      size="small"
                      value={beatMeta(asset)?.source === 'manual'
                        ? String(beatMeta(asset).rotationBeats)
                        : 'auto'}
                      onChange={(event) => handleRotationChange(asset, event.target.value)}
                      disabled={savingAssetId === asset.id || !asset.has_mix_cues}
                      sx={{ color: 'var(--rs-text)', minWidth: 120 }}
                    >
                      <MenuItem value="auto">Automatic</MenuItem>
                      <MenuItem value="0">0 — authored grid</MenuItem>
                      <MenuItem value="1">+1 beat</MenuItem>
                      <MenuItem value="2">+2 beats</MenuItem>
                      <MenuItem value="3">+3 beats</MenuItem>
                    </Select>
                    <Tooltip title="Run the automatic cue and outro analysis again">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleReanalyse(asset)}
                          disabled={
                            savingAssetId === asset.id
                            || !asset.has_mix_cues
                            || beatMeta(asset)?.source === 'manual'
                          }
                          sx={{ color: '#22d3ee' }}
                        >
                          {savingAssetId === asset.id
                            ? <CircularProgress size={16} />
                            : <Refresh fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={tableCellSx}>
                  {asset.is_mix_version ? (
                    <Chip label="Mix" size="small" color="secondary" variant="outlined" />
                  ) : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
